/**
 * utils.js
 *
 * Global cart helper utilities. Loaded as a plain script (not a module) so
 * that `set_tags` and `set_note_attribute` are available anywhere on the
 * storefront — including inline Liquid/HTML blocks such as PageFly elements.
 *
 * Both helpers merge into the cart's existing `attributes` instead of
 * replacing them, since /cart/update.js overwrites the whole attributes
 * object on every call.
 */
(function (window) {
  function getCartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart.js';
  }

  function getCartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  function fetchCart() {
    return fetch(getCartUrl(), { headers: { Accept: 'application/json' } }).then((response) =>
      response.json()
    );
  }

  function postCartAttributes(attributes) {
    return fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).then((response) => response.json());
  }

  /**
   * set_tags({ tags: 'Nutri-Pack,Nutri-Pack-Trial' })
   * Appends the given comma-separated tags to the cart's existing
   * `tags` attribute without dropping tags already present.
   */
  async function set_tags(options) {
    try {
      if (!options || typeof options.tags !== 'string' || !options.tags.trim()) {
        console.warn('set_tags: expected { tags: "tag1,tag2" }');
        return;
      }

      const incomingTags = options.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);

      const cart = await fetchCart();
      const existingAttributes = cart.attributes || {};
      const existingTags = (existingAttributes.tags || '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);

      const mergedTags = Array.from(new Set([...existingTags, ...incomingTags]));

      const mergedAttributes = Object.assign({}, existingAttributes, {
        tags: mergedTags.join(','),
      });

      return await postCartAttributes(mergedAttributes);
    } catch (error) {
      console.error('set_tags error:', error);
    }
  }

  /**
   * set_note_attribute({ key: 'value' })
   * Merges the given key/value pair(s) into the cart's existing
   * note_attributes (cart attributes) without dropping ones already present.
   */
  async function set_note_attribute(options) {
    try {
      if (!options || typeof options !== 'object' || Array.isArray(options)) {
        console.warn('set_note_attribute: expected { key: "value" }');
        return;
      }

      const cart = await fetchCart();
      const existingAttributes = cart.attributes || {};

      const mergedAttributes = Object.assign({}, existingAttributes, options);

      return await postCartAttributes(mergedAttributes);
    } catch (error) {
      console.error('set_note_attribute error:', error);
    }
  }

  window.set_tags = set_tags;
  window.set_note_attribute = set_note_attribute;
})(window);
